// @ts-nocheck
import { contactLine } from "@/components/shared/StackedCell";
import { deadlineStatus } from "@/lib/deadline-status";
import React, { useMemo } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Trash2,
  Printer,
  ChevronDown,
  ChevronRight,
  CheckCircle2,
  Factory,
  Flame,
  Zap,
  Truck,
  Clock,
  AlertTriangle,
  Calendar,
  ShoppingCart,
} from "lucide-react";
import {
  DateGroupedTable,
  DateGroupToggleButton,
  extractDateKey,
} from "@/components/shared/DateGroupedTable";
import { evaluateUrgency } from "@/utils/urgencyEngine";
import { UrgencyBadge } from "@/components/shared/UrgencyBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkActionBar, BulkAction } from "@/components/shared/BulkActionBar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import UserDisplay from "@/components/shared/UserDisplay";
import { formatMoney, formatDate, cn } from "@/lib/utils";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import {
  getStatusConfig,
  getStatusOptions,
  OrderStatus,
  OrderType,
} from "@/config/status-registry";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { type StatusSidebarItem } from "@/components/shared/StatusSidebar";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { useCustomerLookup, useUserLookup } from "@/hooks/useFKLookup";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import type { FilterField } from "@/components/shared/FilterBar";
import {
  downloadExcelTemplate as downloadTemplate,
  type ExcelColumnDef,
} from "@/lib/excel.utils";
import { useExcelExport } from "@/hooks/useExcelExport";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { ResponsiveDataView } from "@/components/shared/ResponsiveDataView";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import { useState } from "react";
import {
  useDatePeriodFilter,
  DatePeriodFilter,
} from "@/components/shared/DatePeriodFilter";
import { OrderKanban } from "./components/OrderKanban";

// ── Status sidebar config ─────────────────────────────────────────────────────
const STATUS_ITEMS: StatusSidebarItem[] = Object.entries(OrderStatus).map(
  ([key, cfg]) => ({
    key,
    label: cfg.label,
    color: cfg.color,
    dotColor: cfg.dotColor,
  }),
);

// ── Filter fields (date range + currency) ────────────────────────────────────
// Moved FILTER_FIELDS inside the component to support dynamic options

const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "orderId", label: "Mã ĐH" },
  { key: "customerName", label: "Khách hàng" },
  { key: "salesUserName", label: "NV Kinh doanh" },
  {
    key: "orderDate",
    label: "Ngày đặt",
    type: "date",
    format: (val: any) =>
      val ? new Date(val).toLocaleDateString("vi-VN") : "",
  },
  {
    key: "deliveryDeadline",
    label: "Hạn giao ĐH",
    type: "date",
    format: (val: any) =>
      val ? new Date(val).toLocaleDateString("vi-VN") : "",
  },
  { key: "status", label: "Trạng thái" },
  { key: "paymentStatus", label: "Thanh toán" },
  { key: "orderType", label: "Loại đơn" },
  { key: "category", label: "Phân loại" },
  { key: "totalAmount", label: "Tổng tiền ĐH", type: "decimal" },
  // Chi tiết sản phẩm
  { key: "productCode", label: "Mã SP" },
  { key: "productName", label: "Tên SP" },
  { key: "specs", label: "Quy cách" },
  { key: "unit", label: "ĐVT" },
  { key: "poReference", label: "PO" },
  { key: "soReference", label: "SO" },
  { key: "customerPo", label: "PO Khách" },
  { key: "quantity", label: "SL Đặt", type: "number" },
  { key: "producedQty", label: "SL Đã SX", type: "number" },
  { key: "qtyDelivered", label: "SL Đã giao", type: "number" },
  {
    key: "qtyMissing",
    label: "SL Thiếu",
    type: "number",
    format: (_: any, row: any) =>
      Math.max(0, (row?.quantity || 0) - (row?.qtyDelivered || 0)),
  },
  { key: "unitPrice", label: "Đơn giá", type: "decimal" },
  {
    key: "itemTotal",
    label: "Thành tiền",
    type: "decimal",
    format: (_: any, row: any) =>
      (row?.quantity || 0) * (row?.unitPrice || 0),
  },
  { key: "itemDeadline", label: "Hạn giao SP" },
  { key: "notes", label: "Ghi chú" },
];

// ── Deadline color helper ─────────────────────────────────────────────────────
function getDeadlineStyle(
  deadline: string | null | undefined,
  status: string,
  deliveredDate?: string | null,
  isAtRisk?: boolean,
) {
  if (!deadline) return { cls: "text-slate-600", label: "" };
  const dl = new Date(deadline);
  // Số ngày còn lại theo NGÀY lịch Việt Nam (cùng cách tính với bộ lọc Đã trễ ở máy chủ)
  const diffDays = deadlineStatus(deadline).days ?? 0;

  // Completed / delivered
  if (["COMPLETED", "DELIVERED"].includes(status)) {
    // Check if delivered after deadline
    if (deliveredDate && new Date(deliveredDate) > dl) {
      return { cls: "text-orange-600 font-bold", label: "Giao trễ" };
    }
    return { cls: "text-emerald-600", label: "" };
  }

  // Cancelled
  if (status === "CANCELLED")
    return { cls: "text-slate-600 line-through", label: "" };

  // Overdue
  if (diffDays < 0) {
    return {
      cls: "text-red-600 font-bold",
      label: `Trễ ${Math.abs(diffDays)}d`,
    };
  }

  // Lệnh dự kiến xong (lịch công đoạn) sau hạn giao
  if (isAtRisk) {
    return { cls: "text-orange-600 font-bold", label: "Nguy cơ trễ" };
  }

  // Approaching (≤ 3 days)
  if (diffDays <= 3) {
    return {
      cls: "text-amber-600 font-bold",
      label: diffDays === 0 ? "Hôm nay" : `Còn ${diffDays}d`,
    };
  }

  // Normal
  return { cls: "text-slate-600", label: "" };
}

const getProductSummary = (orderDetails: any[]) => {
  if (!orderDetails || orderDetails.length === 0) return "—";
  return orderDetails
    .map(
      (d) =>
        `${d.productName || "—"} (${(d.quantity || 0).toLocaleString("vi-VN")})`,
    )
    .join(", ");
};

import { useConfigSuggestions } from "@/hooks/useConfigSuggestions";
import { useConfirm } from "@/providers/ConfirmProvider";

// ── Component ─────────────────────────────────────────────────────────────────
export default function OrderListPage({
  embedded = false,
  customerId,
}: {
  embedded?: boolean;
  customerId?: string;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [workflowTab, setWorkflowTab] = useState<
    "all" | "pendingApproval" | "readyForPrepress" | "inProduction" | "readyForDelivery" | "completed"
  >("all");
  const [deadlineFilter, setDeadlineFilter] = useState<
    "all" | "urgent" | "onTime" | "approaching" | "overdue"
  >("all");
  const [activeView, setActiveView] = useState<"table" | "kanban">("table");
  const [isGrouped, setIsGrouped] = useState(true);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedChildIds, setSelectedChildIds] = useState<Set<string>>(
    new Set(),
  );
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);

  const toggleSelectChild = (id: string) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllChild = (childIds: string[]) => {
    setSelectedChildIds((prev) => {
      const next = new Set(prev);
      const allSelected = childIds.every((id) => next.has(id));
      if (allSelected) {
        childIds.forEach((id) => next.delete(id));
      } else {
        childIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  const toggleExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const {
    period,
    setPeriod,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    dateParams,
  } = useDatePeriodFilter("all");

  const { options: customerOptions } = useCustomerLookup();
  const { options: salesUserOptions } = useUserLookup("SALES");

  const currencies = useConfigSuggestions("CURRENCIES", ["VND", "USD", "JPY"]);
  const orderTypes = useConfigSuggestions("ORDER_TYPES", [
    "PRODUCTION",
    "SAMPLE",
    "REPLACEMENT",
  ]);

  const filterFields: FilterField[] = React.useMemo(
    () => [
      { key: "orderId", label: "Mã ĐH", type: "text", placeholder: "DH-..." },
      {
        key: "customerId",
        label: "Khách hàng",
        type: "combobox-multi",
        options: customerOptions.map((c) => ({ value: c.id, label: c.label })),
      },
      { key: "poNumber", label: "Số PO", type: "text", placeholder: "PO-..." },
      {
        key: "productName",
        label: "Sản phẩm",
        type: "text",
        placeholder: "Tên SP...",
      },
      { key: "orderDate", label: "Ngày đặt", type: "date-range" },
      { key: "deliveryDeadline", label: "Hạn giao", type: "date-range" },
      {
        key: "currency",
        label: "Ngoại tệ",
        type: "combobox-multi",
        options: currencies.map((c) => ({ value: c, label: c })),
      },
      {
        key: "status",
        label: "Trạng thái",
        type: "combobox-multi",
        options: getStatusOptions("OrderStatus", false).filter(o =>
          ["DRAFT", "CONFIRMED", "IN_PRODUCTION", "COMPLETED", "CANCELLED"].includes(o.value)
        ),
      },
      {
        key: "orderType",
        label: "Loại ĐH",
        type: "combobox-multi",
        options: orderTypes.map((t) => ({ value: t, label: t })),
      },
      {
        key: "salesUserId",
        label: "NV Kinh doanh",
        type: "combobox-multi",
        options: salesUserOptions.map((u) => ({ value: u.id, label: u.label })),
      },
    ],
    [currencies, orderTypes, customerOptions, salesUserOptions],
  );

  // ── Server-side Data + Pagination + Search + Filters ──
  const {
    items: allOrders,
    paginatedItems,
    isLoading,
    searchQuery,
    filterValues,
    serverParams,
    showFilter,
    currentPage,
    pageSize,
    totalItems,
    statusCounts: serverStatusCounts,
    statusFilter,
    onStatusChange,
    onFilterChange,
    onPageChange,
    onPageSizeChange,
    onToggleFilter,
    onClearAll,
    filtered,
    sortField,
    sortOrder,
    handleSort,
    meta,
    refetch,
  } = useServerListFilter<any>({
    queryKey: "orders",
    queryFn: (params) => trpcClient.orders.list.query(params) as any,
    extraParams: {
      workflowTab: workflowTab !== "all" ? workflowTab : undefined,
      deadlineFilter: deadlineFilter !== "all" ? deadlineFilter : undefined,
      ...(dateParams.from
        ? { orderDateFrom: dateParams.from, orderDateTo: dateParams.to }
        : {}),
      ...(customerId ? { customerId } : {}),
    },
  });

  const rowIds = paginatedItems.map((o: any) => o.id);
  const rowSelection = useRowSelection(rowIds);
  const {
    selectedIds,
    selectionMode,
    clearSelection,
    selectedCount,
    toggleSelect,
    toggleSelectAll,
    allSelected,
    handleRowSelect,
  } = rowSelection;

  // Items are filtered and paginated server-side according to workflowTab and deadlineFilter
  const displayItems = filtered;

  const handlePrintOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setIsPrintModalOpen(true);
  };

  const flattenOrdersForExport = React.useCallback((ordersList: any[]) => {
    const data: any[] = [];
    (ordersList || []).forEach((order: any) => {
      const customerName = order.customer?.name || order.customer?.fullName || order.customer?.shortName || "";
      const salesUserName = order.salesUser?.fullName || order.salesUser?.name || "";
      const details = order.orderDetails || order.items || [];
      if (details.length > 0) {
        details.forEach((d: any) => {
          data.push({
            ...order,
            id: order.id,
            customerName,
            salesUserName,
            productCode: d.product?.productCode || d.productCode || "",
            productName: d.product?.productName || d.productName || "",
            specs: d.specs || "",
            unit: d.unit || "",
            poReference: d.poReference || "",
            soReference: d.soReference || "",
            customerPo: d.customerPo || "",
            quantity: d.quantity || 0,
            producedQty: d.producedQty || 0,
            qtyDelivered: d.qtyDelivered || 0,
            unitPrice: d.unitPrice || 0,
            itemDeadline: d.deliveryDeadline ? new Date(d.deliveryDeadline).toLocaleDateString("vi-VN") : "",
            notes: d.notes || "",
          });
        });
      } else {
        data.push({
          ...order,
          id: order.id,
          customerName,
          salesUserName,
        });
      }
    });
    return data;
  }, []);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_don_hang",
    sheetName: "Đơn Hàng",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.orders.list.query(params),
    currentFilters: serverParams,
    transformData: flattenOrdersForExport,
    selectedIds,
    idKey: "id",
  });

  // ── Delete ──
  const handleDelete = async (id: string) => {
    try {
      await trpcClient.orders.delete.mutate({ id });
      toast.success("Đã xóa bản ghi thành công.");

      await queryClient.invalidateQueries({ queryKey: ["orders"] });
    } catch (err: any) {
      toast.error(err?.message || "Xoá thất bại");
    }
  };

  // ── Bulk actions ──
  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];
    const selectedItems = (filtered || paginatedItems || []).filter((q: any) => selectedIds.has(q.id));

    if (hasPermission("orders", "approve")) {
      const canConfirm = selectedItems.length > 0 && selectedItems.every((q: any) => ["DRAFT", "PENDING"].includes(q.status));
      if (canConfirm) {
        actions.push({
          label: "Phê duyệt (Confirm)",
          icon: <CheckCircle2 className="w-3.5 h-3.5" />,
          onClick: async () => {
            await executeBulkAction({
              selectedIds: Array.from(selectedIds),
              bulkMutationFn: (ids) => trpcClient.orders.bulkUpdateStatus.mutate({ ids: ids as string[], status: "CONFIRMED" }),
              onSuccess: () => {
                clearSelection();
                refetch();
              }
            });
          }
        });
      }
    }

    if (hasPermission("production", "create") && selectedCount === 1) {
      const q = selectedItems[0];
      if (q && q.status === "CONFIRMED") {
        actions.push({
          label: "Tạo Lệnh Sản Xuất",
          icon: <Factory className="w-3.5 h-3.5" />,
          onClick: () => {
            navigate({ to: "/production-orders/create", search: { salesOrderId: q.id } as any });
          }
        });
      }
    }

    if (hasPermission("orders", "delete")) {
      actions.push({
        label: "Xoá",
        icon: <Trash2 className="w-3.5 h-3.5" />,
        onClick: async () => {
          if (
            !(await confirm(
              `Bạn có chắc muốn xoá ${selectedCount} đơn hàng đã chọn?`,
            ))
          )
            return;

          await executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            bulkMutationFn: (ids) => trpcClient.orders.bulkDelete.mutate({ ids: ids as string[] }),
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["orders"] });
            },
            messages: {
              loading: `Đang xoá ${selectedCount} đơn hàng...`,
              success: `Đã xoá thành công ${selectedCount} đơn hàng`,
            },
          });
        },
        variant: "destructive",
      });
    }

    if (selectedCount === 1 && hasPermission("productionOrders", "create")) {
      actions.push({
        label: "Tạo Lệnh SX",
        icon: <Factory className="w-3.5 h-3.5" />,
        onClick: () => {
          const selectedId = Array.from(selectedIds)[0];
          navigate({ to: "/production-orders/create", search: { salesOrderId: selectedId } as any });
        },
        variant: "default",
      });
    }

    return actions;
  };

  const COLS = 9;

  const filterTags = React.useMemo(() => {
    return STATUS_ITEMS.map((item) => {
      const sc = serverStatusCounts?.find(
        (s: any) =>
          s.status?.toString()?.toUpperCase() === item.key?.toUpperCase(),
      );
      return {
        ...item,
        count: sc ? sc.count : 0,
      };
    });
  }, [serverStatusCounts]);
  const workflowFilterTags = useMemo(() => {
    const wCounts = (meta as any)?.workflowCounts || {
      pendingApproval: 0,
      readyForPrepress: 0,
      inProduction: 0,
      readyForDelivery: 0,
      completed: 0,
      all: 0,
    };
    return [
      {
        key: "pendingApproval",
        label: "Chờ Duyệt",
        count: wCounts.pendingApproval || 0,
        tone: "amber" as const,
      },
      {
        key: "readyForPrepress",
        label: "Chờ Bình File",
        count: wCounts.readyForPrepress || 0,
        tone: "blue" as const,
      },
      {
        key: "inProduction",
        label: "Đang Sản Xuất",
        count: wCounts.inProduction || 0,
        tone: "indigo" as const,
      },
      {
        key: "readyForDelivery",
        label: "Chờ Xuất Giao",
        count: wCounts.readyForDelivery || 0,
        tone: "cyan" as const,
      },
      {
        key: "completed",
        label: "Hoàn Thành",
        count: wCounts.completed || 0,
        tone: "emerald" as const,
      },
    ];
  }, [meta]);

  return (
    <>
      <ListPageShell
        trashModule="orders"
        embedded={embedded}
        dateFilter={
          <div className="flex items-center gap-1.5 shrink-0 flex-nowrap [&_button]:!h-7">
            <DateGroupToggleButton
              isGrouped={isGrouped}
              onToggle={() => setIsGrouped(!isGrouped)}
              className="h-7 px-2 text-xs"
            />
            <DatePeriodFilter
              period={period}
              setPeriod={setPeriod}
              customFrom={customFrom}
              setCustomFrom={setCustomFrom}
              customTo={customTo}
              setCustomTo={setCustomTo}
            />
          </div>
        }
        headerFilters={
          <div className="flex items-center gap-1 shrink-0 flex-nowrap">
            {/* Trạng thái ĐH: hơn 5 lựa chọn → danh sách thả xuống; hạn giao 4 lựa chọn → tag màu */}
            <PageFilterTags
              items={workflowFilterTags}
              selectedKeys={workflowTab === "all" ? [] : [workflowTab]}
              onSelect={(key) => setWorkflowTab((key as any) || "all")}
              dropdownLabel="Trạng thái ĐH"
            />
            <div className="w-[1px] h-5 bg-slate-200 mx-0.5 shrink-0" />
            <button
              type="button"
              onClick={() => setDeadlineFilter(deadlineFilter === "urgent" ? "all" : "urgent")}
              className={cn(
                "flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-semibold border transition-all shrink-0 cursor-pointer whitespace-nowrap",
                deadlineFilter === "urgent"
                  ? "bg-rose-600 text-white border-rose-600 shadow-xs"
                  : "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100",
              )}
              title="Đơn hàng có độ ưu tiên khẩn cấp hoặc hạn giao <= 48 giờ"
              data-testid="urgent-quick-filter-btn"
            >
              <Flame className={cn("h-3.5 w-3.5 shrink-0", deadlineFilter === "urgent" ? "text-white" : "text-rose-500")} />
              <span>Gấp:</span>
              <span className="font-mono font-bold">{(meta as any)?.deadlineCounts?.urgent || 0}</span>
            </button>

            <button
              type="button"
              onClick={() => setDeadlineFilter(deadlineFilter === "approaching" ? "all" : "approaching")}
              className={cn(
                "flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-medium border transition-all shrink-0 cursor-pointer whitespace-nowrap",
                deadlineFilter === "approaching"
                  ? "bg-amber-500 text-white border-amber-500 shadow-xs"
                  : "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100",
              )}
              title="Hạn giao trong vòng 3 ngày tới"
            >
              <Clock className={cn("h-3.5 w-3.5 shrink-0", deadlineFilter === "approaching" ? "text-white" : "text-amber-500")} />
              <span>Sắp trễ:</span>
              <span className="font-mono font-bold">{(meta as any)?.deadlineCounts?.approaching || 0}</span>
            </button>

            <button
              type="button"
              onClick={() => setDeadlineFilter(deadlineFilter === "overdue" ? "all" : "overdue")}
              className={cn(
                "flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-medium border transition-all shrink-0 cursor-pointer whitespace-nowrap",
                deadlineFilter === "overdue"
                  ? "bg-red-600 text-white border-red-600 shadow-xs"
                  : "bg-red-50 text-red-700 border-red-200 hover:bg-red-100",
              )}
              title="Đơn hàng đã quá hạn giao"
            >
              <AlertTriangle className={cn("h-3.5 w-3.5 shrink-0", deadlineFilter === "overdue" ? "text-white" : "text-red-500")} />
              <span>Đã trễ:</span>
              <span className="font-mono font-bold">{(meta as any)?.deadlineCounts?.overdue || 0}</span>
            </button>

            <button
              type="button"
              onClick={() => setDeadlineFilter(deadlineFilter === "onTime" ? "all" : "onTime")}
              className={cn(
                "flex items-center gap-1 h-7 px-2 rounded-md text-[11px] font-medium border transition-all shrink-0 cursor-pointer whitespace-nowrap",
                deadlineFilter === "onTime"
                  ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                  : "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100",
              )}
              title="Đơn hàng còn thời hạn an toàn"
            >
              <CheckCircle2 className={cn("h-3.5 w-3.5 shrink-0", deadlineFilter === "onTime" ? "text-white" : "text-emerald-600")} />
              <span>Đúng hạn:</span>
              <span className="font-mono font-bold">{(meta as any)?.deadlineCounts?.onTime || 0}</span>
            </button>
          </div>
        }
        unitLabel="ĐH"
        actions={
          <div className="flex items-center gap-1.5 shrink-0 flex-nowrap">
            <ExportImportButtons
              onExport={handleExport}
              exportLoading={isExporting}
              onDownloadTemplate={() => downloadTemplate(EXPORT_COLUMNS, "don_hang")}
            >
              {hasPermission("orders", "create") && (
                <Button
                  size="sm"
                  className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-medium shrink-0"
                  onClick={() => navigate({ to: "/orders/create" })}
                >
                  <Plus size={14} className="mr-1 shrink-0" /> Tạo đơn
                </Button>
              )}
            </ExportImportButtons>
          </div>
        }
        filterFields={filterFields}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        activeView={activeView}
        onViewChange={setActiveView as any}
        availableViews={["table", "kanban"]}
      >
        {activeView === "kanban" && (
          <div className="flex-1 overflow-hidden h-[calc(100vh-14rem)]">
            <OrderKanban items={allOrders} />
          </div>
        )}

        {/* ── Responsive Data View ── */}
        {activeView === "table" && (
          <ResponsiveDataView
            items={paginatedItems}
            emptyState={
              searchQuery
                ? `Không tìm thấy đơn hàng "${searchQuery}"`
                : workflowTab !== "all"
                  ? `Không có đơn hàng nào trong mục này`
                  : "Chưa có đơn hàng"
            }
            renderMobileCard={(o: any) => {
              const totalQty =
                o.orderDetails?.reduce(
                  (s: number, d: any) => s + (d.quantity || 0),
                  0,
                ) || 0;
              const deliveredQty =
                o.orderDetails?.reduce(
                  (s: number, d: any) => s + (d.qtyDelivered || 0),
                  0,
                ) || 0;
              const deliveryPct =
                totalQty > 0 ? Math.round((deliveredQty / totalQty) * 100) : 0;
              const dlStyle = getDeadlineStyle(
                o.deliveryDeadline,
                o.status,
                o.deliveredDate,
                o.isAtRisk,
              );

              return (
                <div
                  className={cn(
                    "flex flex-col bg-white border rounded-xl shadow-sm overflow-hidden active:scale-[0.99] transition-transform",
                    selectedIds.has(o.id)
                      ? "border-indigo-400 ring-1 ring-indigo-400"
                      : "border-slate-200",
                  )}
                  onClick={async (e) => {
                    if (selectionMode) {
                      handleRowSelect(o.id, e);
                      return;
                    }
                    navigate({
                      to: "/orders/$orderId",
                      params: { orderId: o.id } as any,
                    });
                  }}
                >
                  <div className="flex items-center justify-between p-3 border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-2">
                      {selectionMode && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedIds.has(o.id)}
                            onCheckedChange={() => toggleSelect(o.id)}
                          />
                        </div>
                      )}
                      <span className="font-bold text-slate-800 text-sm">
                        {o.orderId}
                      </span>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <StatusBadge
                        type="OrderStatus"
                        value={o.status}
                        className="text-[10px] px-1.5 py-0 min-h-0 h-5"
                      />
                      {((o.status === "COMPLETED" || (o.completionPercent ?? 0) >= 100) && o.status !== "DELIVERED" && o.status !== "CANCELLED") && (
                        <Badge className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-[9px] px-1.5 py-0 h-4 mt-0.5 border-0 flex items-center gap-1 shadow-xs">
                          <Truck className="w-2.5 h-2.5" /> SẴN SÀNG XUẤT GIAO
                        </Badge>
                      )}
                    </div>
                  </div>

                  <div className="p-3 flex flex-col gap-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex flex-col">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">
                          Khách hàng
                        </span>
                        <span className="text-[13px] font-semibold text-slate-900 leading-tight">
                          {o.customer?.shortName ||
                            o.customer?.name ||
                            o.customerName ||
                            "—"}
                        </span>
                        {contactLine(o.contactPerson || o.customer?.contactPerson, o.phone || o.customer?.phone) && (
                          <span className="text-[11px] text-slate-500 mt-0.5">
                            {contactLine(o.contactPerson || o.customer?.contactPerson, o.phone || o.customer?.phone)}
                          </span>
                        )}
                        {o.salesUser?.fullName && (
                          <UserDisplay
                            name={o.salesUser.fullName}
                            avatar={o.salesUser.avatar}
                            size="xs"
                            textColor="text-slate-500"
                            className="mt-0.5"
                          />
                        )}
                      </div>
                      <div className="flex flex-col items-end shrink-0">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">
                          Hạn giao
                        </span>
                        <span
                          className={cn("text-[13px] font-medium", dlStyle.cls)}
                        >
                          {o.deliveryDeadline
                            ? formatDate(o.deliveryDeadline)
                            : "—"}
                        </span>
                        {dlStyle.label && (
                          <span
                            className={cn(
                              "text-[9px] px-1.5 py-0 mt-0.5 rounded-sm font-bold whitespace-nowrap",
                              dlStyle.cls.includes("red")
                                ? "bg-red-50 text-red-600"
                                : dlStyle.cls.includes("amber")
                                  ? "bg-amber-50 text-amber-600"
                                  : dlStyle.cls.includes("orange")
                                    ? "bg-orange-50 text-orange-600"
                                    : "bg-slate-50 text-slate-700",
                            )}
                          >
                            {dlStyle.label}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-1 pt-2.5 border-t border-slate-100 border-dashed">
                      <div className="flex flex-col">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">
                          Giao hàng
                        </span>
                        <div className="flex items-center gap-2">
                          <span
                            className={cn(
                              "font-mono font-bold text-[13px]",
                              deliveryPct >= 100
                                ? "text-emerald-600"
                                : deliveryPct > 0
                                  ? "text-amber-600"
                                  : "text-slate-500",
                            )}
                          >
                            {deliveredQty.toLocaleString()}{" "}
                            <span className="text-[11px] text-slate-400 font-normal">
                              / {totalQty.toLocaleString()}
                            </span>
                          </span>

                          <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={cn(
                                "h-full rounded-full",
                                deliveryPct >= 100
                                  ? "bg-emerald-500"
                                  : deliveryPct > 0
                                    ? "bg-amber-400"
                                    : "bg-slate-200",
                              )}
                              style={{
                                width: `${Math.min(deliveryPct, 100)}%`,
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col items-end">
                        <span className="text-[9px] text-slate-500 uppercase tracking-wider mb-0.5">
                          Tổng tiền
                        </span>
                        <span className="font-mono font-bold text-[13px] text-slate-900">
                          {formatMoney(o.totalAmount, o.currency)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }}
          >
            <table
              className={cn(
                "w-full text-xs caption-bottom apv-compact-table table-fixed min-w-[1550px]",
                selectionMode && "apv-select-mode",
              )}
            >
              <colgroup>
                <col className="apv-col-checkbox" />
                <col className="w-[180px]" />
                <col className="w-[220px]" />
                <col className="w-[230px]" />
                <col className="w-[140px]" />
                <col className="w-[95px]" />
                <col className="w-[100px]" />
                <col className="w-[95px]" />
                <col className="w-[95px]" />
                <col className="w-[130px]" />
                <col className="w-[140px]" />
                <col className="w-[85px]" />
              </colgroup>
              <thead className="apv-table-header">
                <tr>
                  <DataTableSelectAllCheckbox rowSelection={rowSelection} />
                  <SortableHeader
                    label="Đơn hàng"
                    field="orderId"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-left apv-col-code"
                  />
                  <SortableHeader
                    label="Khách hàng"
                    field="customer.shortName"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-left"
                  />
                  <th className="text-left px-2 py-1.5">Sản phẩm</th>
                  <SortableHeader
                    label="Hạn giao"
                    field="deliveryDeadline"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-left"
                  />
                  <SortableHeader
                    label="SL Đặt"
                    field="quantity"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHeader
                    label="Thành phẩm"
                    field="qtyManufactured"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHeader
                    label="Đã giao"
                    field="qtyDelivered"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHeader
                    label="Còn thiếu"
                    field="qtyMissing"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <SortableHeader
                    label="Thành tiền"
                    field="totalAmount"
                    currentField={sortField}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                    className="text-right"
                  />
                  <th className="text-left px-2 py-1.5">Ghi chú</th>
                  <th className="text-right px-2 py-1.5 apv-col-actions"></th>
                </tr>
                {showFilter && (
                  <InlineFilterRow
                    columns={[
                      CHECKBOX_COL_NULL,
                      { key: "orderId" },
                      { key: "customerName" },
                      { key: "productName" },
                      { key: "deliveryDeadline" },
                      null,
                      null,
                      null,
                      null,
                      null,
                      null,
                      null,
                    ]}
                    filterFields={filterFields}
                    filterValues={filterValues}
                    onFilterChange={onFilterChange}
                  />
                )}
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 10 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 12 }).map((_, j) => (
                          <td key={j} className="px-2 py-2">
                            <Skeleton className="h-3 w-14" />
                          </td>
                        ))}
                      </tr>
                    ))
                  : (
                      <DateGroupedTable<any>
                        items={paginatedItems}
                        isGrouped={isGrouped}
                        getDateKey={(o) => extractDateKey(o.orderDate || o.createdAt)}
                        renderGroupHeader={(group) => {
                          const groupTotalQty = group.items.reduce(
                            (s, o) =>
                              s +
                              (o.orderDetails?.reduce(
                                (acc: number, d: any) => acc + (d.quantity || 0),
                                0,
                              ) || 0),
                            0,
                          );
                          const groupCompletedQty = group.items.reduce(
                            (s, o) =>
                              s +
                              (o.orderDetails?.reduce(
                                (acc: number, d: any) => acc + (d.completedQty || 0),
                                0,
                              ) || 0),
                            0,
                          );
                          const groupDeliveredQty = group.items.reduce(
                            (s, o) =>
                              s +
                              (o.orderDetails?.reduce(
                                (acc: number, d: any) => acc + (d.qtyDelivered || 0),
                                0,
                              ) || 0),
                            0,
                          );
                          const groupMissingQty = Math.max(0, groupTotalQty - groupDeliveredQty);
                          const groupTotalAmount = group.items.reduce(
                            (s, o) => s + Number(o.totalAmount || 0),
                            0,
                          );

                          return (
                            <tr
                              key={`group-${group.dateKey}`}
                              className={cn(
                                "border-y font-semibold text-xs transition-colors",
                                group.isToday
                                  ? "bg-[#eef2ff] hover:bg-[#e0e7ff] border-indigo-200"
                                  : "bg-[#f1f5f9] hover:bg-[#e2e8f0] border-slate-300",
                              )}
                            >
                              <td colSpan={5} className="py-2 px-3 text-slate-800">
                                <div className="flex items-center gap-2">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-5 w-5 p-0 text-slate-600 hover:text-slate-900 shrink-0"
                                    onClick={group.toggleCollapse}
                                    title={group.isCollapsed ? "Bung ra" : "Thu gọn"}
                                  >
                                    {group.isCollapsed ? (
                                      <ChevronRight size={15} />
                                    ) : (
                                      <ChevronDown size={15} />
                                    )}
                                  </Button>
                                  <span className="font-bold text-slate-800 text-[13px] flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-indigo-600" />
                                    {group.formattedDate}
                                  </span>
                                  <Badge
                                    variant="outline"
                                    className="bg-white/80 text-slate-700 border-slate-300 text-[11px] font-bold px-2 py-0"
                                  >
                                    {group.count} đơn hàng
                                  </Badge>
                                  {group.isToday && (
                                    <Badge className="bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0 border-0">
                                      HÔM NAY
                                    </Badge>
                                  )}
                                  {group.isCollapsed && (
                                    <span className="text-[11px] text-slate-500 font-normal italic">
                                      (Đang thu gọn - bấm để xem chi tiết)
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-slate-800">
                                {groupTotalQty.toLocaleString("vi-VN")}
                              </td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-emerald-700">
                                {groupCompletedQty.toLocaleString("vi-VN")}
                              </td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-blue-700">
                                {groupDeliveredQty.toLocaleString("vi-VN")}
                              </td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-rose-600">
                                {groupMissingQty > 0
                                  ? groupMissingQty.toLocaleString("vi-VN")
                                  : "0"}
                              </td>
                              <td className="py-2 px-2 text-right font-mono font-bold text-slate-900">
                                {formatMoney(groupTotalAmount)}
                              </td>
                              <td className="py-2 px-2"></td>
                              <td className="py-2 px-2"></td>
                            </tr>
                          );
                        }}
                        renderPageSummary={(items) => {
                          const pageTotalQty = items.reduce(
                            (s, o) =>
                              s +
                              (o.orderDetails?.reduce(
                                (acc: number, d: any) => acc + (d.quantity || 0),
                                0,
                              ) || 0),
                            0,
                          );
                          const pageCompletedQty = items.reduce(
                            (s, o) =>
                              s +
                              (o.orderDetails?.reduce(
                                (acc: number, d: any) => acc + (d.completedQty || 0),
                                0,
                              ) || 0),
                            0,
                          );
                          const pageDeliveredQty = items.reduce(
                            (s, o) =>
                              s +
                              (o.orderDetails?.reduce(
                                (acc: number, d: any) => acc + (d.qtyDelivered || 0),
                                0,
                              ) || 0),
                            0,
                          );
                          const pageMissingQty = Math.max(0, pageTotalQty - pageDeliveredQty);
                          const pageTotalAmount = items.reduce(
                            (s, o) => s + Number(o.totalAmount || 0),
                            0,
                          );

                          return (
                            <tr className="bg-slate-100 font-bold border-t-2 border-slate-300 text-xs">
                              <td
                                colSpan={5}
                                className="py-2.5 px-3 text-slate-800 text-right uppercase tracking-wider font-semibold"
                              >
                                Tổng cộng trang ({items.length} đơn):
                              </td>
                              <td className="py-2.5 px-2 text-right font-mono text-slate-900 font-bold">
                                {pageTotalQty.toLocaleString("vi-VN")}
                              </td>
                              <td className="py-2.5 px-2 text-right font-mono text-emerald-700 font-bold">
                                {pageCompletedQty.toLocaleString("vi-VN")}
                              </td>
                              <td className="py-2.5 px-2 text-right font-mono text-blue-700 font-bold">
                                {pageDeliveredQty.toLocaleString("vi-VN")}
                              </td>
                              <td className="py-2.5 px-2 text-right font-mono text-rose-600 font-bold">
                                {pageMissingQty.toLocaleString("vi-VN")}
                              </td>
                              <td className="py-2.5 px-2 text-right font-mono text-slate-900 font-black text-[13px]">
                                {formatMoney(pageTotalAmount)}
                              </td>
                              <td colSpan={2}></td>
                            </tr>
                          );
                        }}
                        emptyState={
                          <tr>
                            <td
                              colSpan={12}
                              className="text-center text-slate-600 py-12 text-sm"
                            >
                              {searchQuery
                                ? `Không tìm thấy đơn hàng "${searchQuery}"`
                                : deadlineFilter !== "all"
                                  ? `Không có đơn hàng theo hạn giao đã chọn`
                                  : workflowTab !== "all"
                                    ? `Không có đơn hàng trong giai đoạn này`
                                    : "Chưa có đơn hàng"}
                            </td>
                          </tr>
                        }
                        renderRow={(o: any) => {
                          const totalQty =
                            o.orderDetails?.reduce(
                              (s: number, d: any) => s + (d.quantity || 0),
                              0,
                            ) || 0;
                          const deliveredQty =
                            o.orderDetails?.reduce(
                              (s: number, d: any) => s + (d.qtyDelivered || 0),
                              0,
                            ) || 0;
                          const completedQty =
                            o.orderDetails?.reduce(
                              (s: number, d: any) => s + (d.completedQty || 0),
                              0,
                            ) || 0;
                          const deliveryPct =
                            totalQty > 0
                              ? Math.round((deliveredQty / totalQty) * 100)
                              : 0;
                          const deliveryCount = o.deliveries?.length || 0;
                          const dlStyle = getDeadlineStyle(
                            o.deliveryDeadline,
                            o.status,
                            o.deliveredDate,
                            o.isAtRisk,
                          );

                          const getStatusBg = (st: string) => {
                            if (dlStyle.cls.includes("red"))
                              return "bg-red-50/30"; // Trễ hẹn ưu tiên đỏ
                            switch (st) {
                              case "CONFIRMED":
                                return "bg-blue-50/30";
                              case "IN_PRODUCTION":
                                return "bg-indigo-50/30";
                              case "PARTIAL_DELIVERY":
                                return "bg-amber-50/30";
                              case "DELIVERED":
                                return "bg-teal-50/30";
                              case "COMPLETED":
                                return "bg-emerald-50/40";
                              case "CANCELLED":
                                return "bg-slate-100/50";
                              default:
                                return "bg-white";
                            }
                          };
                          const urgency = evaluateUrgency({
                            priority: o.priority,
                            deadline: o.deliveryDeadline,
                            status: o.status,
                          });
                          const isCriticalUrgent = urgency.level === "CRITICAL_URGENT";
                          const rowBg = urgency.isUrgent
                            ? urgency.rowBgClass
                            : getStatusBg(o.status);

                          return (
                            <React.Fragment key={o.id}>
                              <DataTableRow
                                className={cn("border-b-0", urgency.borderLeftClass, rowBg)}
                                id={o.id}
                                rowSelection={rowSelection}
                                onClick={() =>
                                  navigate({
                                    to: "/orders/$orderId",
                                    params: { orderId: o.id } as any,
                                  })
                                }
                              >
                                {/* ☑ Checkbox */}
                                <DataTableRowCheckbox
                                  id={o.id}
                                  rowSelection={rowSelection}
                                />

                                {/* Col 2: Mã ĐH + Ngày đặt */}
                                <td className="py-1 pr-2 pl-0 w-[180px] apv-col-code">
                                  <div className="flex items-start gap-1 mt-0.5">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-4 rounded p-0 m-0 text-slate-500 shrink-0"
                                      onClick={(e) => toggleExpand(o.id, e)}
                                    >
                                      {expandedIds.has(o.id) ? (
                                        <ChevronDown size={14} />
                                      ) : (
                                        <ChevronRight size={14} />
                                      )}
                                    </Button>
                                    <div className="flex flex-col items-start gap-0.5 overflow-hidden w-full">
                                      <div className="flex items-center gap-1">
                                        <span
                                          className={cn(
                                            "leading-tight whitespace-nowrap",
                                            isCriticalUrgent
                                              ? "text-red-700 font-extrabold"
                                              : "font-semibold text-slate-900",
                                          )}
                                        >
                                          {o.orderId}
                                        </span>
                                        {urgency.iconType === "flame" && (
                                          <span title="Đơn hàng Hỏa tốc">
                                            <Flame className="h-3.5 w-3.5 text-red-600 shrink-0 animate-pulse" />
                                          </span>
                                        )}
                                        {urgency.iconType === "zap" && (
                                          <span title="Đơn hàng Ưu tiên cao">
                                            <Zap className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-[10px] text-slate-500 font-medium truncate" data-testid="order-row-date-sale">
                                        {formatDate(o.orderDate)}
                                        {o.salesUser?.fullName ? ` · ${o.salesUser.fullName}` : ""}
                                      </span>
                                      <StatusBadge
                                        type="OrderStatus"
                                        value={o.status}
                                        className="text-[9px] px-1.5 py-0 min-h-0 h-4 mt-0.5"
                                      />
                                      {((o.status === "COMPLETED" || (o.completionPercent ?? 0) >= 100) && o.status !== "DELIVERED" && o.status !== "CANCELLED") && (
                                        <Badge className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-[9px] px-1.5 py-0 h-4 mt-0.5 border-0 flex items-center gap-1 shadow-xs whitespace-nowrap">
                                          <Truck className="w-2.5 h-2.5" /> SẴN SÀNG XUẤT GIAO
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="px-2 py-1 min-w-[200px] break-words whitespace-normal leading-tight text-[12px]">
                                  <div className="flex flex-col gap-0.5">
                                    <span
                                      className={cn(
                                        "leading-tight",
                                        isCriticalUrgent
                                          ? "text-red-700 font-extrabold"
                                          : "font-semibold text-slate-900",
                                      )}
                                      title={o.customer?.name || o.customerName}
                                    >
                                      {o.customer?.shortName ||
                                        o.customer?.name ||
                                        o.customerName ||
                                        "—"}
                                    </span>
                                    {/* Dòng phụ: người liên hệ · SĐT của đơn (không có thì của khách hàng) */}
                                    {contactLine(o.contactPerson || o.customer?.contactPerson, o.phone || o.customer?.phone) && (
                                      <span className="text-[11px] text-slate-500 truncate" data-testid="order-row-contact">
                                        {contactLine(o.contactPerson || o.customer?.contactPerson, o.phone || o.customer?.phone)}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-1">
                                  {(() => {
                                    if (
                                      !o.orderDetails ||
                                      o.orderDetails.length === 0
                                    ) {
                                      return (
                                        <span className="text-slate-400">
                                          —
                                        </span>
                                      );
                                    }
                                    const visible = o.orderDetails.slice(0, 2);
                                    const remaining = o.orderDetails.length - 2;
                                    return (
                                      <div className="flex flex-col gap-0.5 text-xs text-slate-700">
                                        {visible.map((d: any, idx: number) => (
                                          <div
                                            key={idx}
                                            className="min-w-0"
                                            title={`${d.productCode ? `${d.productCode} · ` : ""}${d.productName || "—"} (${(d.quantity || 0).toLocaleString("vi-VN")})`}
                                          >
                                            {/* Mã SP nổi bật ở dòng trên, tên SP ở dòng dưới: đơn hàng theo dõi theo mã sản phẩm (FB-VFAP3A) */}
                                            {d.productCode && d.productCode !== d.productName ? (
                                              <>
                                                <div className="flex items-center gap-1 min-w-0">
                                                  <span
                                                    data-testid="order-list-product-code"
                                                    className={cn(
                                                      "font-mono text-[12px] font-bold bg-indigo-50 border border-indigo-200 px-1 py-px rounded truncate",
                                                      isCriticalUrgent ? "text-red-700" : "text-indigo-800",
                                                    )}
                                                  >
                                                    {d.productCode}
                                                  </span>
                                                  <span className="text-slate-500 text-[11px] shrink-0">
                                                    ({(d.quantity || 0).toLocaleString("vi-VN")})
                                                  </span>
                                                </div>
                                                <div className="truncate text-[11px] text-slate-600">{d.productName || "—"}</div>
                                              </>
                                            ) : (
                                              <div className="truncate">
                                                <span className={cn(isCriticalUrgent ? "text-red-700 font-bold" : "font-medium")}>
                                                  {d.productName || "—"}
                                                </span>
                                                <span className="text-slate-500 ml-1 text-[11px]">
                                                  ({(d.quantity || 0).toLocaleString("vi-VN")})
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                        {remaining > 0 && (
                                          <div
                                            className="text-[10px] text-indigo-500 font-semibold mt-0.5 hover:underline"
                                            onClick={(e) =>
                                              toggleExpand(o.id, e)
                                            }
                                          >
                                            + {remaining} sản phẩm khác
                                          </div>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </td>
                                <td className="px-2 py-1 overflow-hidden">
                                  <div className="flex flex-col gap-0.5 items-start mt-0.5 min-w-0">
                                    <span
                                      className={cn(
                                        "text-xs",
                                        isCriticalUrgent
                                          ? "text-red-700 font-extrabold"
                                          : "font-semibold text-slate-900",
                                      )}
                                    >
                                      {o.deliveryDeadline
                                        ? formatDate(o.deliveryDeadline)
                                        : "—"}
                                    </span>
                                    <UrgencyBadge
                                      priority={o.priority}
                                      deadline={o.deliveryDeadline}
                                      status={o.status}
                                      className="text-[10px] px-1.5 py-0 max-w-full"
                                    />
                                    {o.isAtRisk && (
                                      <span
                                        className="text-[10px] font-bold text-orange-600 whitespace-nowrap"
                                        data-testid="order-at-risk"
                                        title={o.plannedFinishAt ? `Lệnh dự kiến xong ${formatDate(o.plannedFinishAt)} — sau hạn giao` : "Lệnh dự kiến xong sau hạn giao"}
                                      >
                                        Nguy cơ trễ
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="px-2 py-1 text-right w-[85px]">
                                  <div className="font-mono font-bold text-slate-700">
                                    {totalQty.toLocaleString()}
                                  </div>
                                </td>
                                <td className="px-2 py-1 text-right w-[100px]">
                                  <div className="flex flex-col items-end gap-0.5 mt-0.5">
                                    <span className="font-mono font-bold text-emerald-600">
                                      {completedQty.toLocaleString()}
                                    </span>
                                    <span className="text-[10px] font-medium text-slate-500">
                                      {totalQty > 0
                                        ? Math.round(
                                            (completedQty / totalQty) * 100,
                                          )
                                        : 0}
                                      % HT
                                    </span>
                                  </div>
                                </td>
                                <td className="px-2 py-1 text-right w-[85px]">
                                  <div className="flex flex-col items-end gap-0.5 mt-0.5">
                                    <div
                                      className={cn(
                                        "font-mono font-bold leading-tight",
                                        deliveryPct >= 100
                                          ? "text-emerald-600"
                                          : deliveryPct > 0
                                            ? "text-amber-600"
                                            : "text-slate-500",
                                      )}
                                    >
                                      {deliveredQty.toLocaleString()}
                                    </div>
                                    <span className="text-[10px] font-medium text-slate-500">
                                      {deliveryPct}% GH
                                    </span>
                                  </div>
                                  {deliveryCount > 0 && (
                                    <div className="text-[9px] text-indigo-500 font-semibold">
                                      {deliveryCount} PGH
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-1 text-right w-[85px]">
                                  <div
                                    className={cn(
                                      "font-mono font-bold",
                                      totalQty > deliveredQty
                                        ? "text-rose-500"
                                        : "text-slate-300",
                                    )}
                                  >
                                    {totalQty > deliveredQty
                                      ? (
                                          totalQty - deliveredQty
                                        ).toLocaleString()
                                      : "—"}
                                  </div>
                                </td>
                                <td className="px-2 py-1 text-right w-[110px]">
                                  <div className="font-mono font-bold text-slate-900 leading-tight">
                                    {formatMoney(o.totalAmount, o.currency)}
                                  </div>
                                </td>
                                <td className="px-2 py-1 w-[120px]">
                                  <div
                                    className="text-[9px] text-slate-500 truncate max-w-[120px]"
                                    title={o.notes || ""}
                                  >
                                    {o.notes || "—"}
                                  </div>
                                </td>
                                <td
                                  className="px-2 py-1 text-right apv-col-actions"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="flex items-center justify-end gap-0 apv-row-actions">
                                    {hasPermission("orders", "update") &&
                                      !["COMPLETED", "CANCELLED"].includes(
                                        o.status,
                                      ) && (
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                          title="Chốt đơn (Force Close)"
                                          onClick={async (e) => {
                                            e.stopPropagation();
                                            if (
                                              !(await confirm(
                                                `Bạn có chắc muốn chốt đơn hàng ${o.orderId}?`,
                                              ))
                                            )
                                              return;
                                            try {
                                              await trpcClient.orders.updateStatus.mutate(
                                                {
                                                  id: o.id,
                                                  status: "COMPLETED",
                                                },
                                              );

                                              queryClient.invalidateQueries({
                                                queryKey: ["orders"],
                                              });
                                            } catch (err: any) {
                                              toast.error(
                                                err?.message ||
                                                  "Lỗi khi chốt đơn",
                                              );
                                            }
                                          }}
                                        >
                                          <CheckCircle2 size={12} />
                                        </Button>
                                      )}
                                    {hasPermission("orders", "delete") && (
                                      <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <Trash2
                                              size={12}
                                              className="text-destructive"
                                            />
                                          </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <AlertDialogHeader>
                                            <AlertDialogTitle>
                                              Xác nhận xoá
                                            </AlertDialogTitle>
                                            <AlertDialogDescription>
                                              Bạn có chắc muốn xoá đơn hàng{" "}
                                              {o.orderId}?
                                            </AlertDialogDescription>
                                          </AlertDialogHeader>
                                          <AlertDialogFooter>
                                            <AlertDialogCancel>
                                              Huỷ
                                            </AlertDialogCancel>
                                            <AlertDialogAction
                                              onClick={() => handleDelete(o.id)}
                                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            >
                                              Xoá
                                            </AlertDialogAction>
                                          </AlertDialogFooter>
                                        </AlertDialogContent>
                                      </AlertDialog>
                                    )}
                                  </div>
                                </td>
                              </DataTableRow>
                              {expandedIds.has(o.id) && (
                                <tr className="bg-slate-50/60 border-b border-slate-100">
                                  <td colSpan={12} className="p-3 pl-12 pr-6">
                                    <div className="bg-white rounded-lg border border-slate-200/80 shadow-sm overflow-hidden">
                                      <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/80 flex items-center justify-between">
                                        <span className="font-bold text-slate-800 text-[11px] uppercase tracking-wider">
                                          Chi tiết đơn hàng: {o.orderId}
                                        </span>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-[11px] text-slate-600 min-w-[1000px] table-fixed">
                                          <colgroup>
                                            <col className="apv-col-checkbox" />
                                            <col className="w-[50px]" />
                                            <col className="w-[120px]" />
                                            <col className="w-auto" />
                                            <col className="w-[140px]" />
                                            <col className="w-[95px]" />
                                            <col className="w-[95px]" />
                                            <col className="w-[90px]" />
                                            <col className="w-[90px]" />
                                            <col className="w-[90px]" />
                                            <col className="w-[90px]" />
                                            <col className="w-[80px]" />
                                            <col className="w-[95px]" />
                                            <col className="w-[110px]" />
                                            <col className="w-[150px]" />
                                          </colgroup>
                                          <thead className="bg-slate-50 text-slate-500 font-semibold border-b border-slate-200">
                                            <tr>
                                              <th
                                                className="apv-col-checkbox w-[40px] px-2 py-2 text-center"
                                                onClick={(e) =>
                                                  e.stopPropagation()
                                                }
                                              >
                                                <Checkbox
                                                  checked={
                                                    o.orderDetails?.length >
                                                      0 &&
                                                    o.orderDetails.every(
                                                      (d: any, i: number) =>
                                                        selectedChildIds.has(
                                                          d.id ||
                                                            d.orderDetailId ||
                                                            String(i),
                                                        ),
                                                    )
                                                  }
                                                  onCheckedChange={() =>
                                                    toggleSelectAllChild(
                                                      o.orderDetails?.map(
                                                        (d: any, i: number) =>
                                                          d.id ||
                                                          d.orderDetailId ||
                                                          String(i),
                                                      ) || [],
                                                    )
                                                  }
                                                />
                                              </th>
                                              <th className="px-2 py-2 text-center">
                                                STT
                                              </th>
                                              <th className="px-2 py-2 text-left">
                                                Mã SP
                                              </th>
                                              <th className="px-2 py-2 text-left">
                                                Tên Sản phẩm
                                              </th>
                                              <th className="px-2 py-2 text-left">
                                                PO / SO
                                              </th>
                                              <th className="px-2 py-2 text-left">
                                                Trạng thái
                                              </th>
                                              <th className="px-2 py-2 text-left">
                                                Hạn giao hàng
                                              </th>
                                              <th className="px-2 py-2 text-right">
                                                SL Yêu cầu
                                              </th>
                                              <th className="px-2 py-2 text-right">
                                                Thành phẩm
                                              </th>
                                              <th className="px-2 py-2 text-right">
                                                SL Đã giao
                                              </th>
                                              <th className="px-2 py-2 text-right">
                                                Còn thiếu
                                              </th>
                                              <th className="px-2 py-2 text-right">
                                                Tiến độ
                                              </th>
                                              <th className="px-2 py-2 text-right">
                                                Đơn giá
                                              </th>
                                              <th className="px-2 py-2 text-right">
                                                Thành tiền
                                              </th>
                                              <th className="px-2 py-2 text-left">
                                                Ghi chú
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {!o.orderDetails ||
                                            o.orderDetails.length === 0 ? (
                                              <tr>
                                                <td
                                                  colSpan={13}
                                                  className="text-center py-4 text-slate-400"
                                                >
                                                  Không có chi tiết sản phẩm
                                                </td>
                                              </tr>
                                            ) : (
                                              o.orderDetails.map(
                                                (d: any, i: number) => {
                                                  const oQty = d.quantity || 0;
                                                  const dDeliv =
                                                    d.qtyDelivered || 0;
                                                  const compQty =
                                                    d.completedQty || 0;
                                                  const miss = Math.max(
                                                    0,
                                                    oQty - dDeliv,
                                                  );
                                                  const progress =
                                                    oQty > 0
                                                      ? Math.round(
                                                          (dDeliv / oQty) * 100,
                                                        )
                                                      : 0;
                                                  const dStatus =
                                                    d.status || "PENDING";
                                                  const stCfg = getStatusConfig(
                                                    "OrderStatus",
                                                    dStatus,
                                                  );
                                                  return (
                                                    <tr
                                                      key={i}
                                                      className="border-b border-slate-100/50 last:border-0 hover:bg-slate-50"
                                                    >
                                                      <td
                                                        className="apv-col-checkbox px-2 py-2"
                                                        onClick={(e) =>
                                                          e.stopPropagation()
                                                        }
                                                      >
                                                        <Checkbox
                                                          checked={selectedChildIds.has(
                                                            d.id ||
                                                              d.orderDetailId ||
                                                              String(i),
                                                          )}
                                                          onCheckedChange={() =>
                                                            toggleSelectChild(
                                                              d.id ||
                                                                d.orderDetailId ||
                                                                String(i),
                                                            )
                                                          }
                                                        />
                                                      </td>
                                                      <td className="px-2 py-1.5 text-center text-slate-400 font-mono">
                                                        {i + 1}
                                                      </td>
                                                      <td className="px-2 py-1.5 font-mono text-slate-500 truncate">
                                                        {d.productCode || "—"}
                                                      </td>
                                                      <td className="px-2 py-1.5 text-slate-700 text-xs break-words">
                                                        {d.productName || "—"}
                                                      </td>
                                                      <td className="px-2 py-1.5 font-mono text-slate-500">
                                                        <div className="flex flex-col gap-0.5">
                                                          {(d.poNumber ||
                                                            d.poReference) && (
                                                            <div className="flex items-center gap-1">
                                                              <span className="text-[9px] text-slate-400 w-4">
                                                                PO
                                                              </span>
                                                              <span className="text-slate-700 truncate">
                                                                {d.poNumber ||
                                                                  d.poReference}
                                                              </span>
                                                            </div>
                                                          )}
                                                          {(d.soNumber ||
                                                            d.soReference) && (
                                                            <div className="flex items-center gap-1">
                                                              <span className="text-[9px] text-slate-400 w-4">
                                                                SO
                                                              </span>
                                                              <span className="text-slate-700 truncate">
                                                                {d.soNumber ||
                                                                  d.soReference}
                                                              </span>
                                                            </div>
                                                          )}
                                                          {!(
                                                            d.poNumber ||
                                                            d.poReference ||
                                                            d.soNumber ||
                                                            d.soReference
                                                          ) && (
                                                            <span className="text-slate-300">
                                                              —
                                                            </span>
                                                          )}
                                                        </div>
                                                      </td>
                                                      <td className="px-2 py-1.5">
                                                        <span
                                                          className={cn(
                                                            "px-1.5 py-0.5 rounded text-[9px] font-medium whitespace-nowrap",
                                                            stCfg.color,
                                                          )}
                                                        >
                                                          {stCfg.label}
                                                        </span>
                                                      </td>
                                                      <td className="px-2 py-1.5 text-slate-700 whitespace-nowrap">
                                                        {formatDate(
                                                          d.deliveryDeadline ||
                                                            d.deliveryDate,
                                                        ) || "—"}
                                                      </td>
                                                      <td className="text-right px-2 py-1.5 font-mono">
                                                        {oQty.toLocaleString()}
                                                      </td>
                                                      <td className="text-right px-2 py-1.5 font-mono font-medium text-emerald-600">
                                                        {compQty.toLocaleString()}
                                                      </td>
                                                      <td className="text-right px-2 py-1.5 font-mono font-medium text-indigo-600">
                                                        {dDeliv.toLocaleString()}
                                                      </td>
                                                      <td className="text-right px-2 py-1.5 font-mono font-bold text-rose-600">
                                                        {miss.toLocaleString()}
                                                      </td>
                                                      <td className="text-right px-2 py-1.5 font-mono">
                                                        <span
                                                          className={cn(
                                                            "px-1.5 py-0.5 rounded text-[10px] font-bold",
                                                            progress >= 100
                                                              ? "bg-emerald-100 text-emerald-700"
                                                              : progress > 0
                                                                ? "bg-amber-100 text-amber-700"
                                                                : "bg-slate-100 text-slate-500",
                                                          )}
                                                        >
                                                          {progress}%
                                                        </span>
                                                      </td>
                                                      <td className="text-right px-2 py-1.5 font-mono">
                                                        {formatMoney(
                                                          d.unitPrice || 0,
                                                        )}
                                                      </td>
                                                      <td className="text-right px-2 py-1.5 font-mono font-bold text-slate-700">
                                                        {formatMoney(
                                                          oQty *
                                                            (d.unitPrice || 0),
                                                        )}
                                                      </td>
                                                      <td className="px-2 py-1.5 text-[9px] text-slate-500 italic break-words">
                                                        {d.notes || "—"}
                                                      </td>
                                                    </tr>
                                                  );
                                                },
                                              )
                                            )}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        }}
                      />
                    )}
              </tbody>
            </table>
          </ResponsiveDataView>
        )}
      </ListPageShell>
      <BulkActionBar
        selectedCount={selectedCount}
        onClearSelection={clearSelection}
        actions={getBulkActions()}
      />
    </>
  );
}
